import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const API = process.env.REACT_APP_BACKEND_URL;

/* ==================== DAILY TAB ==================== */
function DailyTab() {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => { fetchPreview(); }, []);

  const fetchPreview = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/daily-report/preview`, { credentials: 'include' });
      if (res.ok) setPreview(await res.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const handleSend = async () => {
    if (!window.confirm('Enviar reporte diario ahora a Javierh@iqeslowvoltage.com?')) return;
    setSending(true);
    try {
      const res = await fetch(`${API}/api/daily-report/send`, { method: 'POST', credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setMsg(data.status === 'sent' ? `Reporte enviado | ${data.leads} leads, ${data.emails} emails` : `Error: ${data.error}`);
      } else { setMsg('Error al enviar'); }
    } catch (e) { setMsg('Error de conexion'); }
    setSending(false);
    setTimeout(() => setMsg(''), 8000);
  };

  if (loading) return <div className="flex justify-center py-16"><div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" /></div>;

  const p = preview || {};
  const pipeline = p.lead_pipeline || {};
  const quoteSt = p.quote_statuses || {};
  const stepColors = { new: 'bg-blue-600', contacted: 'bg-purple-600', qualified: 'bg-emerald-600', quote_pending_approval: 'bg-orange-600', quote_sent: 'bg-cyan-600', quote_signed: 'bg-green-600', invoiced: 'bg-indigo-600', completed: 'bg-emerald-700', rejected: 'bg-red-600' };

  return (
    <div className="space-y-5">
      <div className="flex justify-between items-center">
        <p className="text-slate-400 text-sm">Ultimas 24 horas | Envio automatico: 8:00 AM EST</p>
        <div className="flex gap-2">
          <Button data-testid="refresh-daily-btn" variant="outline" size="sm" onClick={fetchPreview}>Actualizar</Button>
          <Button data-testid="send-daily-btn" size="sm" onClick={handleSend} disabled={sending} className="bg-blue-600 hover:bg-blue-700 text-white">
            {sending ? 'Enviando...' : 'Enviar Diario Ahora'}
          </Button>
        </div>
      </div>
      {msg && <div className={`px-4 py-2 rounded text-sm ${msg.includes('Error') ? 'bg-red-900/30 border border-red-700 text-red-200' : 'bg-green-900/30 border border-green-700 text-green-200'}`}>{msg}</div>}

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { val: p.new_leads || 0, label: 'Leads Nuevos', bg: 'bg-blue-950/40', border: 'border-blue-800', text: 'text-blue-300' },
          { val: p.new_prospects || 0, label: 'Prospectos B2B', bg: 'bg-purple-950/40', border: 'border-purple-800', text: 'text-purple-300' },
          { val: p.emails_today || 0, label: 'Emails', bg: 'bg-green-950/40', border: 'border-green-800', text: 'text-green-300' },
          { val: p.sms_today || 0, label: 'SMS', bg: 'bg-amber-950/40', border: 'border-amber-800', text: 'text-amber-300' },
          { val: p.calls_today || 0, label: 'Llamadas', bg: 'bg-red-950/40', border: 'border-red-800', text: 'text-red-300' },
        ].map((kpi, i) => (
          <Card key={i} className={`${kpi.bg} ${kpi.border}`}>
            <CardContent className="p-4 text-center">
              <p className={`text-3xl font-bold ${kpi.text}`}>{kpi.val}</p>
              <p className="text-slate-400 text-xs mt-1 uppercase">{kpi.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="bg-slate-900/60 border-slate-700">
        <CardHeader className="pb-3"><CardTitle className="text-white text-base">Pipeline CRM</CardTitle></CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {Object.entries(pipeline).sort((a, b) => b[1] - a[1]).map(([step, count]) => (
              <span key={step} className={`${stepColors[step] || 'bg-slate-600'} text-white px-3 py-1.5 rounded-full text-xs font-semibold`}>{step}: {count}</span>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="bg-slate-900/60 border-slate-700">
        <CardHeader className="pb-3"><CardTitle className="text-white text-base">Cotizaciones</CardTitle></CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {Object.entries(quoteSt).sort((a, b) => b[1] - a[1]).map(([st, count]) => {
              const qc = { draft: 'bg-slate-500', 'Pending Approval': 'bg-amber-600', Approved: 'bg-green-600', Sent: 'bg-blue-600', Signed: 'bg-emerald-600', Rejected: 'bg-red-600', 'Changes Requested': 'bg-orange-600' };
              return <span key={st} className={`${qc[st] || 'bg-slate-600'} text-white px-3 py-1.5 rounded-full text-xs font-semibold`}>{st}: {count}</span>;
            })}
          </div>
        </CardContent>
      </Card>

      {(p.new_leads_detail || []).length > 0 && (
        <Card className="bg-slate-900/60 border-slate-700">
          <CardHeader className="pb-3"><CardTitle className="text-white text-base">Leads Nuevos Hoy</CardTitle></CardHeader>
          <CardContent>
            <table className="w-full"><thead><tr className="text-slate-400 text-xs uppercase border-b border-slate-700">
              <th className="text-left p-2">Nombre</th><th className="text-left p-2">Email</th><th className="text-left p-2">Fuente</th><th className="text-left p-2">Estado</th>
            </tr></thead><tbody>
              {(p.new_leads_detail || []).map((ld, i) => (
                <tr key={i} className="border-b border-slate-800"><td className="p-2 text-white text-xs">{ld.name || 'N/A'}</td><td className="p-2 text-slate-300 text-xs">{ld.email || ''}</td><td className="p-2 text-slate-300 text-xs">{ld.source || ''}</td><td className="p-2"><span className="bg-blue-900/50 text-blue-300 px-2 py-0.5 rounded text-xs">{ld.pipeline_step || ld.status || 'new'}</span></td></tr>
              ))}
            </tbody></table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

/* ==================== WEEKLY TAB ==================== */
function WeeklyTab() {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => { fetchPreview(); }, []);

  const fetchPreview = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/weekly-report/preview`, { credentials: 'include' });
      if (res.ok) setPreview(await res.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const handleSend = async () => {
    if (!window.confirm('Enviar reporte semanal ahora a Javierh@iqeslowvoltage.com?')) return;
    setSending(true);
    try {
      const res = await fetch(`${API}/api/weekly-report/send`, { method: 'POST', credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setMsg(data.status === 'sent' ? `Reporte semanal enviado | ${data.leads} leads, $${data.revenue?.toLocaleString()} revenue` : `Error: ${data.error}`);
      } else { setMsg('Error al enviar'); }
    } catch (e) { setMsg('Error de conexion'); }
    setSending(false);
    setTimeout(() => setMsg(''), 8000);
  };

  if (loading) return <div className="flex justify-center py-16"><div className="w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" /></div>;

  const p = preview || {};
  const tw = p.this_week || {};
  const lw = p.last_week || {};
  const daily = p.daily || { labels: [], leads: [], emails: [], calls: [] };

  const pctChange = (curr, prev) => {
    if (prev === 0) return curr > 0 ? { text: '+100%', color: 'text-green-400' } : { text: '0%', color: 'text-slate-400' };
    const pct = ((curr - prev) / prev) * 100;
    if (pct > 0) return { text: `+${pct.toFixed(0)}%`, color: 'text-green-400' };
    if (pct < 0) return { text: `${pct.toFixed(0)}%`, color: 'text-red-400' };
    return { text: '0%', color: 'text-slate-400' };
  };

  const maxLead = Math.max(...daily.leads, 1);
  const maxEmail = Math.max(...daily.emails, 1);
  const maxCall = Math.max(...daily.calls, 1);

  return (
    <div className="space-y-5">
      <div className="flex justify-between items-center">
        <p className="text-slate-400 text-sm">Periodo: {p.date_range || 'N/A'} | Envio automatico: Lunes 9:00 AM EST</p>
        <div className="flex gap-2">
          <Button data-testid="refresh-weekly-btn" variant="outline" size="sm" onClick={fetchPreview}>Actualizar</Button>
          <Button data-testid="send-weekly-btn" size="sm" onClick={handleSend} disabled={sending} className="bg-indigo-600 hover:bg-indigo-700 text-white">
            {sending ? 'Enviando...' : 'Enviar Semanal Ahora'}
          </Button>
        </div>
      </div>
      {msg && <div className={`px-4 py-2 rounded text-sm ${msg.includes('Error') ? 'bg-red-900/30 border border-red-700 text-red-200' : 'bg-green-900/30 border border-green-700 text-green-200'}`}>{msg}</div>}

      {/* Hero KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { val: tw.leads || 0, label: 'Leads', bg: 'from-blue-900 to-blue-700', text: 'text-blue-200', prev: lw.leads || 0 },
          { val: tw.signed || 0, label: 'Firmadas', bg: 'from-purple-900 to-purple-700', text: 'text-purple-200', prev: lw.signed || 0 },
          { val: `$${(tw.revenue || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}`, label: 'Revenue', bg: 'from-green-900 to-green-700', text: 'text-green-200', prev: lw.revenue || 0, curr: tw.revenue || 0, isCurr: true },
          { val: `${p.quote_win_rate || 0}%`, label: 'Win Rate', bg: 'from-amber-900 to-amber-700', text: 'text-amber-200', noChange: true },
        ].map((kpi, i) => {
          const change = kpi.noChange ? null : pctChange(kpi.isCurr ? kpi.curr : (typeof kpi.val === 'number' ? kpi.val : 0), kpi.prev);
          return (
            <div key={i} className={`bg-gradient-to-br ${kpi.bg} rounded-xl p-5 text-center`}>
              <p className={`text-3xl font-extrabold ${kpi.text}`}>{kpi.val}</p>
              <p className="text-slate-400 text-xs mt-1 uppercase tracking-wider">{kpi.label}</p>
              {change && <p className={`text-xs mt-1 font-semibold ${change.color}`}>{change.text} vs semana anterior</p>}
            </div>
          );
        })}
      </div>

      {/* Comparison Table */}
      <Card className="bg-slate-900/60 border-slate-700">
        <CardHeader className="pb-2"><CardTitle className="text-white text-base">Comparacion Semanal</CardTitle></CardHeader>
        <CardContent>
          <table className="w-full">
            <thead><tr className="text-slate-400 text-xs uppercase border-b border-slate-700">
              <th className="text-left p-2">Metrica</th><th className="text-center p-2">Esta Semana</th><th className="text-center p-2">Anterior</th><th className="text-center p-2">Cambio</th>
            </tr></thead>
            <tbody>
              {[
                { label: 'Leads', tw: tw.leads, lw: lw.leads },
                { label: 'Prospectos B2B', tw: tw.prospects, lw: lw.prospects },
                { label: 'Cotizaciones', tw: tw.quotes, lw: lw.quotes },
                { label: 'Firmadas', tw: tw.signed, lw: lw.signed },
                { label: 'Emails', tw: tw.emails, lw: lw.emails },
                { label: 'Llamadas', tw: tw.calls, lw: lw.calls },
                { label: 'SMS', tw: tw.sms, lw: lw.sms },
                { label: 'Revenue', tw: tw.revenue, lw: lw.revenue, isCurr: true },
              ].map((row, i) => {
                const change = pctChange(row.tw || 0, row.lw || 0);
                const fmt = (v) => row.isCurr ? `$${(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}` : (v || 0);
                return (
                  <tr key={i} className="border-b border-slate-800">
                    <td className="p-2 text-white text-xs">{row.label}</td>
                    <td className="p-2 text-center text-white text-xs font-bold">{fmt(row.tw)}</td>
                    <td className="p-2 text-center text-slate-400 text-xs">{fmt(row.lw)}</td>
                    <td className={`p-2 text-center text-xs font-bold ${change.color}`}>{change.text}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Daily Trend Charts */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[
          { title: 'Leads por Dia', data: daily.leads, maxV: maxLead, color: 'bg-blue-500' },
          { title: 'Emails por Dia', data: daily.emails, maxV: maxEmail, color: 'bg-green-500' },
          { title: 'Llamadas por Dia', data: daily.calls, maxV: maxCall, color: 'bg-amber-500' },
        ].map((chart, ci) => (
          <Card key={ci} className="bg-slate-900/60 border-slate-700">
            <CardHeader className="pb-2"><CardTitle className="text-white text-sm">{chart.title}</CardTitle></CardHeader>
            <CardContent>
              <div className="flex items-end gap-1 h-24">
                {chart.data.map((val, di) => (
                  <div key={di} className="flex-1 flex flex-col items-center">
                    <span className="text-[10px] text-slate-300 mb-1">{val}</span>
                    <div className={`w-full ${chart.color} rounded-t`} style={{ height: `${Math.max(4, (val / chart.maxV) * 70)}px` }} />
                    <span className="text-[8px] text-slate-500 mt-1">{daily.labels[di]?.slice(0, 3)}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Conversion Rates */}
      <div className="grid grid-cols-2 gap-4">
        <Card className="bg-slate-900/60 border-slate-700">
          <CardContent className="p-6 text-center">
            <p className="text-slate-400 text-xs uppercase">Tasa Conversion Leads</p>
            <p className="text-green-400 text-4xl font-extrabold mt-2">{p.conversion_rate || 0}%</p>
          </CardContent>
        </Card>
        <Card className="bg-slate-900/60 border-slate-700">
          <CardContent className="p-6 text-center">
            <p className="text-slate-400 text-xs uppercase">Tasa Cierre Cotizaciones</p>
            <p className="text-amber-400 text-4xl font-extrabold mt-2">{p.quote_win_rate || 0}%</p>
          </CardContent>
        </Card>
      </div>

      {/* Pipeline */}
      <Card className="bg-slate-900/60 border-slate-700">
        <CardHeader className="pb-2"><CardTitle className="text-white text-base">Pipeline Actual</CardTitle></CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {Object.entries(p.pipeline || {}).sort((a, b) => b[1] - a[1]).map(([step, count]) => (
              <span key={step} className="bg-slate-700 text-white px-3 py-1.5 rounded-full text-xs font-semibold">{step}: {count}</span>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Top Leads and Prospects */}
      {(p.top_leads || []).length > 0 && (
        <Card className="bg-slate-900/60 border-slate-700">
          <CardHeader className="pb-2"><CardTitle className="text-white text-base">Leads de la Semana ({(p.top_leads || []).length})</CardTitle></CardHeader>
          <CardContent>
            <table className="w-full"><thead><tr className="text-slate-400 text-xs uppercase border-b border-slate-700">
              <th className="text-left p-2">Nombre</th><th className="text-left p-2">Fuente</th><th className="text-left p-2">Servicio</th><th className="text-left p-2">Estado</th>
            </tr></thead><tbody>
              {(p.top_leads || []).map((ld, i) => (
                <tr key={i} className="border-b border-slate-800"><td className="p-2 text-white text-xs">{ld.name || 'N/A'}</td><td className="p-2 text-slate-300 text-xs">{ld.source || ''}</td><td className="p-2 text-slate-300 text-xs">{ld.service_interest || ''}</td><td className="p-2"><span className="bg-blue-900/50 text-blue-300 px-2 py-0.5 rounded text-xs">{ld.pipeline_step || ld.status || ''}</span></td></tr>
              ))}
            </tbody></table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

/* ==================== MAIN PAGE ==================== */
export default function DailyReportPage() {
  const [activeTab, setActiveTab] = useState('daily');

  return (
    <div data-testid="daily-report-page" className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white">Reportes Ejecutivos</h1>
        <p className="text-slate-400 text-sm">Monitoreo y evaluacion CRM + B2B | Destinatario: Javierh@iqeslowvoltage.com</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="daily" data-testid="tab-daily">Reporte Diario</TabsTrigger>
          <TabsTrigger value="weekly" data-testid="tab-weekly">Reporte Semanal</TabsTrigger>
        </TabsList>
        <TabsContent value="daily" className="mt-4">
          <DailyTab />
        </TabsContent>
        <TabsContent value="weekly" className="mt-4">
          <WeeklyTab />
        </TabsContent>
      </Tabs>

      <div className="bg-slate-800/40 border border-slate-700 rounded-lg p-4">
        <p className="text-slate-400 text-xs">
          <strong className="text-white">Diario:</strong> Se envia a las 8:00 AM EST cada dia.
          <strong className="text-white ml-3">Semanal:</strong> Se envia los Lunes a las 9:00 AM EST.
          Ambos incluyen evaluacion IA con GPT-4o. Tambien puede enviarlos manualmente.
        </p>
      </div>
    </div>
  );
}
